import { useMutation } from "@tanstack/react-query";
import { ArrowLeftRight } from "lucide-react";
import { useState } from "react";

import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input, Label } from "@/components/ui/input";
import { useSession } from "@/hooks/useSession";
import { api, type MigrationEnvelope } from "@/lib/api";

interface ImportResult {
  dry_run: boolean; valid?: boolean; source_project?: string;
  app_count?: number;
  diff?: { create: string[]; update: string[];
           conflict: { name: string; reason: string }[];
           secret_refs: string[] };
  result?: { created: string[]; skipped: string[]; conflict_policy: string };
}

export default function Migration() {
  const { user } = useSession();
  const canOperate = user?.role === "owner" || user?.role === "operator";
  const [envelopeText, setEnvelopeText] = useState("");
  const [crossProject, setCrossProject] = useState(false);
  const [confirmProject, setConfirmProject] = useState("");
  const [policy, setPolicy] = useState<"fail" | "skip">("fail");
  const [result, setResult] = useState<ImportResult | null>(null);
  const [err, setErr] = useState("");

  const doExport = useMutation({
    mutationFn: () => api.get<MigrationEnvelope>("/api/migration/export"),
    onSuccess: (d) => {
      const blob = new Blob([JSON.stringify(d, null, 2)],
        { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `nxshield-config-${d.source.project}-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      setErr("");
    },
    onError: (e) => setErr(e.message),
  });

  const doImport = useMutation({
    mutationFn: (dry: boolean) => {
      let env: unknown;
      try { env = JSON.parse(envelopeText); }
      catch { throw new Error("envelope is not valid JSON"); }
      return api.post<ImportResult>("/api/migration/import", {
        envelope: env, dry_run: dry,
        allow_cross_project: crossProject,
        target_project_confirmed: confirmProject,
        conflict_policy: policy,
      });
    },
    onSuccess: (d) => { setResult(d); setErr(""); },
    onError: (e) => { setResult(null); setErr(e.message); },
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title="Configuration migration"
        description="nx.migration — versioned export/import between authorized NXShield installs. Secrets export as name references only. Wrong-project and incompatible envelopes are rejected before anything mutates."
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Export this install</h3>
            {canOperate && (
              <Button size="sm" onClick={() => doExport.mutate()}
                disabled={doExport.isPending}>
                <ArrowLeftRight className="h-3.5 w-3.5" /> Download envelope
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Contains applications, policy profiles and secret <i>names</i> —
            never secret values. The envelope carries this install's project
            identity so a sibling-project import is refused by default.
          </p>
        </Card>

        <Card className="p-4 space-y-3">
          <h3 className="text-sm font-medium">Import an envelope</h3>
          <textarea
            className="h-36 w-full rounded border bg-background p-2 font-mono text-xs"
            placeholder="paste a nxshield-config envelope…"
            value={envelopeText} onChange={(e) => setEnvelopeText(e.target.value)} />
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={crossProject}
                onChange={(e) => setCrossProject(e.target.checked)} />
              cross-project import
            </label>
            {crossProject && (
              <Input className="h-7 w-48 text-xs"
                placeholder="confirm THIS install's project"
                value={confirmProject}
                onChange={(e) => setConfirmProject(e.target.value)} />
            )}
            <Label className="flex items-center gap-1.5">conflicts
              <select className="rounded border bg-background px-1.5 py-0.5"
                value={policy} onChange={(e) => setPolicy(e.target.value as "fail" | "skip")}>
                <option value="fail">fail</option>
                <option value="skip">skip</option>
              </select>
            </Label>
          </div>
          {canOperate && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => doImport.mutate(true)}
                disabled={!envelopeText || doImport.isPending}>Dry-run diff</Button>
              <Button size="sm" onClick={() => doImport.mutate(false)}
                disabled={!envelopeText || doImport.isPending}>Apply import</Button>
            </div>
          )}
          {err && <p className="text-sm text-destructive">{err}</p>}
        </Card>
      </div>

      {result && (
        <Card className="p-4 space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <Badge variant={result.dry_run ? "secondary" : "success"}>
              {result.dry_run ? "dry run — nothing mutated" : "applied"}
            </Badge>
            <span className="text-xs text-muted-foreground">
              source: {result.source_project} · {result.app_count} application(s)
            </span>
          </div>
          {result.diff && (
            <div className="grid gap-2 font-mono text-xs sm:grid-cols-3">
              <div><span className="text-emerald-400">create:</span>{" "}
                {result.diff.create.join(", ") || "—"}</div>
              <div><span className="text-sky-400">update:</span>{" "}
                {result.diff.update.join(", ") || "—"}</div>
              <div><span className="text-amber-300">conflict:</span>{" "}
                {result.diff.conflict.map((c) => c.name).join(", ") || "—"}</div>
            </div>
          )}
          {result.diff?.secret_refs?.length ? (
            <p className="text-xs text-muted-foreground">
              secret references required at target: {result.diff.secret_refs.join(", ")}
            </p>
          ) : null}
          {result.result && (
            <p className="font-mono text-xs">
              created: {result.result.created.join(", ") || "—"} ·
              skipped: {result.result.skipped.join(", ") || "—"}
            </p>
          )}
        </Card>
      )}
    </div>
  );
}
